import { Dispatch } from 'react-redux';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import { Api } from '../../typings/api';
import { SortState } from './reducer';
import { createNotification } from '../NotificationBar/actions';
import UserAgentUtils from '../../utils/userAgent';
import NativeApi from '../../utils/nativeApi';
import UrlUtils from '../../utils/url';
import { checkActiveSession } from '../MultiAppAuthentication/actions';

export enum ProposalStatsActionKeys {
	SET_IS_LOADING = '@@proposalStats/SET_IS_LOADING',
	SET_PROPOSAL_STATUS = '@@proposalStats/SET_PROPOSAL_STATUS',
	SET_PAGINATOR_DATA = '@@proposalStats/SET_PAGINATOR_DATA',
	SET_NEW_PAGE = '@@proposalState/SET_NEW_PAGE',
	ADD_PAGE = '@@proposalStats/ADD_PAGE',
	SORT = '@@proposalStats/SORT',
	RESET = '@@proposalStats/RESET',
}

interface PaginatorData {
	page: number;
	totalPages?: number;
}

interface SetIsLoadingAction extends Action {
	type: ProposalStatsActionKeys.SET_IS_LOADING;
	isLoading: boolean;
}

interface SetProposalStatusAction extends Action {
	type: ProposalStatsActionKeys.SET_PROPOSAL_STATUS;
	status: Api.ProposalStatus;
}

interface SetPaginatorDataAction extends Action {
	type: ProposalStatsActionKeys.SET_PAGINATOR_DATA;
	data: PaginatorData;
}

interface SetNewPageAction extends Action {
	type: ProposalStatsActionKeys.SET_NEW_PAGE;
}

interface AddPageAction extends Action {
	type: ProposalStatsActionKeys.ADD_PAGE;
	list: Api.AspDriveResourceItem[];
}

interface SortAction extends Action {
	type: ProposalStatsActionKeys.SORT;
	sort: SortState;
}

interface ResetAction extends Action {
	type: ProposalStatsActionKeys.RESET;
}

export type ProposalStatsAction =
	SetIsLoadingAction |
	SetProposalStatusAction |
	SetPaginatorDataAction |
	SetNewPageAction |
	AddPageAction |
	SortAction |
	ResetAction;

export const setIsLoading = (isLoading: boolean): SetIsLoadingAction => ({
	type: ProposalStatsActionKeys.SET_IS_LOADING,
	isLoading,
});

export const setPaginatorData = (data: PaginatorData): SetPaginatorDataAction => ({
	type: ProposalStatsActionKeys.SET_PAGINATOR_DATA,
	data,
});

export const setNewPage = (): SetNewPageAction => ({
	type: ProposalStatsActionKeys.SET_NEW_PAGE,
});

export const addPage = (list: Api.AspDriveResourceItem[]): AddPageAction => ({
	type: ProposalStatsActionKeys.ADD_PAGE,
	list,
});

export const getProposals = (page: number = 0) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsLoading(true));
		dispatch(setPaginatorData({
			page,
		}));

		const {
			galleryInfos: { config: { clientId } },
			proposalStats: {
				proposalStatus,
				sort: { sortOn, ascending },
				paginator,
			},
		} = getState();

		const {
			currentPage,
			totalPage,
			list,
		} = await ApiUtils.getProposalsByType(clientId, proposalStatus, sortOn, ascending, page, paginator.itemsPerPage);

		if (page === 0) {
			dispatch(setNewPage());
		}

		dispatch(setPaginatorData({
			page: currentPage,
			totalPages: totalPage,
		}));

		dispatch(addPage(list));
	} catch (error) {
		dispatch(createNotification('error.fetch_proposal_stats_error'));
	} finally {
		dispatch(setIsLoading(false));
	}
};

export const setProposalStatus = (status: Api.ProposalStatus) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		dispatch({
			type: ProposalStatsActionKeys.SET_PROPOSAL_STATUS,
			status,
		});

		await dispatch(getProposals());
	} catch (error) {
		dispatch(createNotification('error.fetch_proposal_stats_error'));
	}
};

export const sort = (sort: SortState) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: ProposalStatsActionKeys.SORT,
			sort,
		});

		await dispatch(getProposals());
	} catch (error) {
		dispatch(createNotification('error.fetch_proposal_stats_error'));
	}
};

export const reset = (): ResetAction => ({
	type: ProposalStatsActionKeys.RESET,
});

export const openPreview = (relUrl: string, type: Api.AssetItemFileTypes) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { galleryInfos: { config } } = getState();
	const relUrlIdOrFile = relUrl.split('/')[1];
	const id = parseFloat(relUrlIdOrFile);
	await dispatch(checkActiveSession());

	let url = '';

	if (type === Api.AssetItemFileTypes.PUBLICATION) {
		const driveItem = {
			id,
		} as Api.DriveItem;
		url = UrlUtils.getPublicationUrl(config, driveItem, 1, UserAgentUtils.isMobileApp);
	} else {
		url = UrlUtils.getItemOriginalFileUrl(config, relUrlIdOrFile);
	}

	if (type === Api.AssetItemFileTypes.PUBLICATION && UserAgentUtils.isMobileApp) {
		NativeApi.openFrame(url);
	} else {
		window.open(url);
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ProposalStatsPage/actions.ts